import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MdSearch,
    MdFileDownload,
    MdFilterList,
    MdArrowBack
} from 'react-icons/md';
import { formatDate } from '../utils/dateUtils';
import { getReportData } from '../utils/reportDemoData';
import { exportToCSV } from '../utils/exportUtils';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import { toast } from 'react-toastify';
import {
    GET_ALL_PROJECT_API,
    GET_ALL_VENDORS_API,
    GET_ALL_POS_API,
    GET_PROJECT_VENDORS_API,
    GET_VENDOR_PAYMENT_OVERDUE_REPORT_API
} from '../config/API';

import styles from './VendorPaymentOverdueReport.module.css';

const VendorPaymentOverdueReport = () => {
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [showFilters, setShowFilters] = useState(false);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedPo, setSelectedPo] = useState(null);


    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const calculateOverdueDays = (date) => {
        if (!date) return 0;
        const today = new Date();
        const poDate = new Date(date);
        const timeDiff = today.getTime() - poDate.getTime();
        const diffDays = Math.floor(timeDiff / (1000 * 3600 * 24));
        return diffDays > 0 ? diffDays : 0;
    };


    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=${limit}&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedProject?.label) queryParams += `&projectName=${selectedProject.label}`;
            if (selectedVendor?.label) queryParams += `&vendorName=${selectedVendor.label}`; // API uses vendorName
            if (selectedPo?.label) queryParams += `&poNumber=${selectedPo.label}`;

            const response = await authFetch(
                `${GET_VENDOR_PAYMENT_OVERDUE_REPORT_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setOrders(result.data || []);
                const total = result.total || result.pagination?.total || result.count || 0;
                setTotalPages(result.pagination?.totalPages || Math.ceil(total / 10) || 1);
                setTotalRecords(total);
            }
        } catch (error) {
            console.error('Error fetching overdue report:', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [currentPage, debouncedSearch, dateRange, selectedProject, selectedVendor, selectedPo, limit]);

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedProject?.label) queryParams += `&projectName=${selectedProject.label}`;
            if (selectedVendor?.label) queryParams += `&vendorName=${selectedVendor.label}`;
            if (selectedPo?.label) queryParams += `&poNumber=${selectedPo.label}`;

            const response = await authFetch(
                `${GET_VENDOR_PAYMENT_OVERDUE_REPORT_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting overdue report:', error);
            toast.error('An error occurred during export');
        }
    };


    return (
        <div className={styles['purchase-orders-container']}>
            <div className={styles['purchase-orders-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button className="back-btn" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}>
                        <MdArrowBack size={20} />
                    </button>
                    <h2>Vendor Payment OverDue Report</h2>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button
                        className={styles['export-btn']}
                        onClick={handleExport}
                    >
                        <MdFileDownload /> Export
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Project</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_PROJECT_API}
                            mapResponse={(p) => ({
                                value: p.id,
                                label: p.projectName
                            })}
                            value={selectedProject}
                            onChange={(option) => setSelectedProject(option)}
                            placeholder="Select Project"
                            isClearable
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>Vendor</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_VENDORS_API}
                            mapResponse={(v) => {
                                const vendor = v.Vendor || v;
                                return {
                                    value: vendor.id,
                                    label: vendor.vendorName || vendor.companyName
                                };
                            }}
                            value={selectedVendor}
                            onChange={(option) => setSelectedVendor(option)}
                            placeholder="Select Vendor"
                            isClearable
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>PO Number</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_POS_API}
                            mapResponse={(po) => ({
                                value: po.id,
                                label: po.poNumber
                            })}
                            value={selectedPo}
                            onChange={(option) => setSelectedPo(option)}
                            placeholder="Search PO Number"
                            isClearable
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>End Date</label>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setDateRange({ start: '', end: '' });
                                setSelectedProject(null);
                                setSelectedVendor(null);
                                setSelectedPo(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset All Filters
                        </button>
                    </div>
                </div>
            </Drawer>

            <div className={styles['card-container-po']}>

                {/* Search Bar */}
                <div className={styles['search-container']}>
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search by PO No, Vendor or Project..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className={styles['clear-search-btn']}
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles['table-container']}>
                    <table>
                        <thead>
                            <tr>
                                <th>Sn</th>
                                <th>PO Number</th>
                                <th>PO Created Date</th>
                                <th>Project Name</th>
                                <th>Vendor Name</th>
                                <th>Amount</th>
                                <th>Over Due Days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <Loader text="Fetching report data..." />
                                    </td>
                                </tr>
                            ) : orders.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <NoData title="No Overdue Payments" message="Try adjusting your search filters." />
                                    </td>
                                </tr>
                            ) : (
                                orders.map((po, index) => {
                                    const overdueDays = po.overdueDays || po.daysDifference || calculateOverdueDays(po.date);
                                    return (
                                        <tr key={po.id}>
                                            <td>{(currentPage - 1) * 10 + index + 1}</td>
                                            <td>
                                                <span className={styles['po-number']}>#{po.poNumber}</span>
                                            </td>
                                            <td>{formatDate(po.date)}</td>
                                            <td>{po.project?.projectName || po.projectName || 'N/A'}</td>
                                            <td>{po.companyName + " " + '(' + (po.vendorName || 'N/A') + ')'}</td>
                                            <td className={styles['amount-cell']}>₹{parseFloat(po.grandTotal || po.amount || 0).toLocaleString()}</td>
                                            <td>
                                                {overdueDays > 0 ? (
                                                    <span className={styles['overdue-badge-urgent']}>{overdueDays} Days</span>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className={styles['mobile-card-view']}>
                    {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <Loader text="Fetching report data..." />
                        </div>
                    ) : orders.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <NoData title="No Overdue Payments" message="Try adjusting your search filters." />
                        </div>
                    ) : (
                        orders.map((po, index) => {
                            const overdueDays = po.overdueDays || po.daysDifference || calculateOverdueDays(po.date);
                            const vendorName = po.RequestVendor?.Vendor?.vendorName || po.vendorName || 'N/A';
                            return (
                                <div key={po.id} className={styles['dashboard-card']}>
                                    <div className={styles['card-header']}>
                                        <div className={styles['card-title']}>
                                            <strong> {(currentPage - 1) * 10 + index + 1} </strong>
                                            <span className={`${styles['status-badge']} ${styles['status-closed']} ${styles['status-badge-receipt']}`}>
                                                #{po.poNumber}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles['card-body']}>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Vendor</span>
                                            <span className={styles['card-value']}>
                                                <strong>{vendorName}</strong>
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Project</span>
                                            <span className={styles['card-value']}>{po.ProjectMaster?.projectName || po.projectName || 'N/A'}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Amount</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell']}`}>₹{parseFloat(po.grandTotal || po.amount || 0).toLocaleString()}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Overdue</span>
                                            <span className={styles['card-value']}>
                                                {overdueDays > 0 ? (
                                                    <span className={styles['overdue-badge-urgent']}>{overdueDays} Days</span>
                                                ) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <Pagination limit={limit} onLimitChange={handleLimitChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                    totalRecords={totalRecords}
                    currentCount={orders.length}
                    entityName="Vendor Payment OverDue Report"
                />
            </div>
        </div>
    );
};

export default VendorPaymentOverdueReport;
